import { useCreateBettingSession } from '@/api/betting-sessions';
import { useBetSuccessCallback } from '@/hooks/bets/useBetSuccessCallback';
import { DetailedBet } from '@/models/detailed-bet';
import { GamingAggregatorId } from '@/models/gaming-aggregator/id';
import { GenericError } from '@/models/generic-error';
import { CreateBetInputDto } from '@/services/dto/bet/create-bet-input.dto';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import useTranslation from 'next-translate/useTranslation';
import { UseOriginalsProps } from './props';
import { fetchCreateBet } from './service';

export function useOriginals(options: UseOriginalsProps) {
  const { lang: language } = useTranslation('common');
  const { onSuccess, gameId } = options ?? {};
  const { onSuccess: onSuccessCallback } = useBetSuccessCallback(gameId);
  const { createBettingSessionAsync, data: sessionData } =
    useCreateBettingSession();

  function handleOnSuccess(data: DetailedBet, variables: CreateBetInputDto) {
    onSuccessCallback(data);

    if (onSuccess) {
      onSuccess(data, variables);
    }
  }

  const {
    mutateAsync,
    isPending: isLoading,
    error,
    data,
    isError,
    reset,
  } = useMutation<DetailedBet, AxiosError<GenericError>, CreateBetInputDto>({
    mutationFn: fetchCreateBet,
    onSuccess: handleOnSuccess,
  });

  async function createBet(input: CreateBetInputDto) {
    try {
      let bettingSession = sessionData;

      if (!bettingSession) {
        bettingSession = await createBettingSessionAsync({
          aggregatorId: GamingAggregatorId.Starsoft,
          lang: language,
          pairId: input?.coinId,
          coinId: input?.coinId,
          gameId: input?.gameId,
        });
      }

      const response = await mutateAsync({
        ...input,
        sessionId: bettingSession?.id,
      });

      return response;
    } catch (e: unknown) {
      return;
    }
  }

  return {
    createBet,
    isLoading,
    reset,
    error,
    isError,
    data,
  };
}
